import { createContext, useContext, useState, useEffect, ReactNode } from "react";

export interface ProjectLinkContextType {
  courseId: string | null;
  resourceLinkId: string | null;
  setProjectLinkParams: (courseId: string | null, resourceLinkId: string | null) => void;
  clearProjectLinkParams: () => void;
}

const STORAGE_KEY = "projectLinkParams";

const ProjectLinkContext = createContext<ProjectLinkContextType | null>(null);

export const useProjectLink = () => {
  const context = useContext(ProjectLinkContext);
  if (!context) {
    throw new Error("useProjectLink must be used within a ProjectLinkProvider");
  }
  return context;
};

interface ProjectLinkProviderProps {
  children: ReactNode;
}

export const ProjectLinkProvider: React.FC<ProjectLinkProviderProps> = ({ children }) => {
  const [courseId, setCourseId] = useState<string | null>(null);
  const [resourceLinkId, setResourceLinkId] = useState<string | null>(null);

  // Load from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const { courseId: storedCourseId, resourceLinkId: storedResourceLinkId } = JSON.parse(stored);
        setCourseId(storedCourseId);
        setResourceLinkId(storedResourceLinkId);
      }
    } catch (error) {
      console.error("Failed to load project link params from localStorage:", error);
    }
  }, []);

  const setProjectLinkParams = (newCourseId: string | null, newResourceLinkId: string | null) => {
    setCourseId(newCourseId);
    setResourceLinkId(newResourceLinkId);

    // Persist to localStorage
    try {
      if (newCourseId && newResourceLinkId) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({
          courseId: newCourseId,
          resourceLinkId: newResourceLinkId
        }));
      } else {
        localStorage.removeItem(STORAGE_KEY);
      }
    } catch (error) {
      console.error("Failed to save project link params to localStorage:", error);
    }
  };

  const clearProjectLinkParams = () => {
    setCourseId(null);
    setResourceLinkId(null);

    // Clear from localStorage
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.error("Failed to clear project link params from localStorage:", error);
    }
  };

  const value: ProjectLinkContextType = {
    courseId,
    resourceLinkId,
    setProjectLinkParams,
    clearProjectLinkParams,
  };

  return (
    <ProjectLinkContext.Provider value={value}>
      {children}
    </ProjectLinkContext.Provider>
  );
};

export default ProjectLinkContext;